/**
 * PendingJobCard - Displays a document that's currently being processed.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2, Clock } from 'lucide-react';
import { Badge, IconContainer } from '@/components/ui';
import type { ProcessingJob } from '@/services/addApi';
import { getStatusDisplay, formatDate } from '../utils';

interface PendingJobCardProps {
  job: ProcessingJob;
}

export const PendingJobCard: React.FC<PendingJobCardProps> = ({ job }) => {
  const { t } = useTranslation();
  const statusDisplay = getStatusDisplay(job.status);

  return (
    <div className="p-4 sm:p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border-2 border-amber-200 border-dashed">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <IconContainer variant="warning" size="sm">
            <Sparkles className="w-5 h-5 animate-pulse" />
          </IconContainer>
          {job.projectName && (
            <Badge variant="default" size="sm" className="bg-white/60">
              {job.projectName}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
        </div>
      </div>

      {/* Title */}
      <h3 className="font-semibold text-slate-900 mb-1 text-sm sm:text-base">
        {job.templateName || t('projects.newDocument')}
      </h3>

      {/* Status */}
      <p className={`text-xs sm:text-sm mb-3 ${statusDisplay.color}`}>
        {job.statusMessage || statusDisplay.message}
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between text-[11px] sm:text-xs text-slate-400">
        <span className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(job.createdAt)}
        </span>
        <Badge variant="warning" size="sm">{t('projects.processingBadge')}</Badge>
      </div>
    </div>
  );
};

export default PendingJobCard;
